<template>
  <transition name="assist-panel-fade">
    <div
      v-if="isVisible"
      class="assist-panel"
      :class="{ 'assist-panel--resizing': isResizing }"
      :style="{ width: `${panelWidth}px` }"
    >
      <div
        class="assist-panel__resize-handle"
        role="separator"
        aria-orientation="vertical"
        :aria-valuenow="panelWidth"
        :aria-valuemin="minPanelWidth"
        :aria-valuemax="maxPanelWidth"
        :aria-label="$t('assist.resizeHandleAriaLabel')"
        tabindex="0"
        @pointerdown="onResizeStart"
        @pointermove="onResizeMove"
        @pointerup="onResizeEnd"
        @pointercancel="onResizeEnd"
        @keydown="onResizeKeydown"
      />
      <!-- 上部: タブナビゲーション -->
      <div class="assist-panel__header">
        <TabNavigation :active-tab="activeTab" @tab-change="handleTabChange" />
      </div>

      <!-- 中央: コンテンツエリア（動的コンテンツ） -->
      <div class="assist-panel__content">
        <!-- Vuex状態に応じたコンテンツ表示 -->
        <component
          :is="currentContentComponent"
          v-bind="currentContentProps"
          @suggestion-click="handleSuggestionClick"
          @confirm-selection="handleConfirmSelection"
          @manuscript-select="handleManuscriptSelect"
          @manuscripts-loaded="handleManuscriptsLoaded"
          @manuscripts-error="handleManuscriptsError"
          @back-to-request="handleBackToRequest"
          @back-click="handleBackToRequest"
        />
      </div>

      <!-- エラー表示 -->
      <div v-if="hasAnyError" class="assist-panel__error">
        <div class="error-content">
          <span class="error-icon">⚠️</span>
          <div class="error-messages">
            <p v-if="error" class="error-message">{{ error }}</p>
            <p v-if="apiError" class="api-error-message">API: {{ apiError }}</p>
          </div>
          <button class="error-dismiss" @click="dismissErrors">×</button>
        </div>
      </div>

      <!-- ローディングオーバーレイ -->
      <div v-if="isAnyLoading" class="assist-panel__loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <p>処理中...</p>
        </div>
      </div>

      <!-- 下部: Fish カウンター -->
      <div class="assist-panel__footer">
        <FishCounter />
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "lodash";
import TabNavigation from "@/components/molecules/TabNavigation.vue";
import FishCounter from "@/components/atoms/FishCounter.vue";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import AssistRequestContent from "@/components/molecules/AssistRequestContent.vue";
import ManuscriptSelectContent from "@/components/molecules/ManuscriptSelectContent.vue";
import ThemeReviewConfirmContent from "@/components/molecules/ThemeReviewConfirmContent.vue";
import ThemeMatchingConfirmContent from "@/components/molecules/ThemeMatchingConfirmContent.vue";
import HistoryListContent from "@/components/molecules/HistoryListContent.vue";
import ResultViewContent from "@/components/molecules/ResultViewContent.vue";
import { AssistContentType } from "@/store/modules/assistNavigation";
import { AssistFunctionType, AssistPanelContext } from "@/lib/models/assist";
import { logError, logWarn } from "@/lib/utils/logger";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";
import { isThemeGenerateRedirectRequested, removeThemeGenerateRedirectQuery } from "@/lib/utils/themeGenerateRedirect";
import {
  ASSIST_PANEL_DEFAULT_WIDTH,
  ASSIST_PANEL_MIN_WIDTH,
  ASSIST_PANEL_MAX_WIDTH,
  clampAssistPanelWidth,
  loadAssistPanelWidth,
  saveAssistPanelWidth,
} from "@/constants/assistPanel";

/** 企画ページ専用（原稿 context では拒否） */
const THEME_ONLY_CONTENTS: readonly AssistContentType[] = [
  "theme-review-confirm",
  "theme-matching-confirm",
];

/**
 * context 専用コンテンツの拒否リスト方式。
 * - 許可リストではないため、新規 AssistContentType / AssistPanelContext 追加時は
 *   下記配列への追記漏れに注意（型エラーにはならない）。
 * - Vuex の currentContent はページ跨ぎで残るため、不一致なら依頼画面へ戻す判定に使う。
 * - manuscript-select は企画項目生成でも使うため、theme context でも許可する。
 */
function isContentAllowedForContext(content: AssistContentType, context: AssistPanelContext): boolean {
  if (context === "manuscript" && THEME_ONLY_CONTENTS.includes(content)) {
    return false;
  }
  return true;
}

interface AssistPanelData {
  analytics: AssistAnalytics | null;
  currentRequestType: "feedback" | "matching" | "catchcopy" | "summary" | "theme-generate" | null;
  panelWidth: number;
  isResizing: boolean;
  resizeStartX: number;
  resizeStartWidth: number;
  resizePointerId: number | null;
  persistPanelWidthDebounced: ReturnType<typeof debounce> | null;
}

interface AssistPanelProps {
  context: AssistPanelContext;
}

interface AssistPanelMethods {
  handleTabChange(tabId: string): void;
  handleSuggestionClick(suggestion: any): void;
  handleConfirmSelection(manuscript: any): void;
  executeNavigationFlow(manuscript: any): Promise<void>;
  handleManuscriptSelect(manuscript: any): void;
  handleManuscriptsLoaded(manuscripts: any[]): void;
  handleManuscriptsError(error: any): void;
  handleBackToRequest(): void;
  dismissErrors(): void;
  clearAllErrors(): void;
  getResultFeedbackText(): string;
  handleRouteChange(newRoute: any, oldRoute?: any): Promise<void>;
  extractNovelIdFromRoute(route: any): string | null;
  initializePanelState(): void;
  cleanupPanelState(): void;
  clampPanelWidth(width: number): number;
  onResizeStart(event: PointerEvent): void;
  onResizeMove(event: PointerEvent): void;
  onResizeEnd(event?: PointerEvent): void;
  onResizeKeydown(event: KeyboardEvent): void;
  onWindowResize(): void;
  persistPanelWidth(): void;
  consumeThemeGenerateRedirect(): Promise<void>;
}

interface AssistPanelComputed {
  activeTab: string;
  currentContent: AssistContentType;
  error: string | null;
  selectedItemId: string | null;
  isApiLoading: boolean;
  apiError: string | null;
  hasAnyError: boolean;
  isAnyLoading: boolean;
  isVisible: boolean;
  currentContentComponent: string;
  currentContentProps: Record<string, any>;
  minPanelWidth: number;
  maxPanelWidth: number;
  hasThemeGenerateRedirectRequest: boolean;
}

export default Vue.extend<AssistPanelData, AssistPanelMethods, AssistPanelComputed, AssistPanelProps>({
  name: "AssistPanel",
  components: {
    TabNavigation,
    FishCounter,
    ChatMessage,
    AssistRequestContent,
    ManuscriptSelectContent,
    ThemeReviewConfirmContent,
    ThemeMatchingConfirmContent,
    HistoryListContent,
    ResultViewContent,
  },
  props: {
    // 表示中ページに応じた依頼メニュー等の出し分け（原稿ページ / 企画ページ）
    context: {
      type: String as () => AssistPanelContext,
      default: "manuscript",
    },
  },
  data(): AssistPanelData {
    return {
      analytics: null,
      currentRequestType: null,
      panelWidth: loadAssistPanelWidth(),
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: ASSIST_PANEL_DEFAULT_WIDTH,
      resizePointerId: null,
      persistPanelWidthDebounced: null,
    };
  },
  computed: {
    // 🔧 Phase 5 修正: activeTabをVuexから取得
    activeTab(): string {
      return this.$store.getters["assistNavigationModule/currentTab"] || "assist";
    },

    // 画面遷移関連のgetters
    currentContent(): AssistContentType {
      return this.$store.getters["assistNavigationModule/currentContent"];
    },
    error(): string | null {
      return this.$store.getters["assistNavigationModule/error"];
    },
    selectedItemId(): string | null {
      return this.$store.getters["assistNavigationModule/selectedItemId"];
    },

    // 原稿選択関連のgetters
    isApiLoading(): boolean {
      return this.$store.getters["assistSelectManuscriptModule/isApiLoading"];
    },
    apiError(): string | null {
      return this.$store.getters["assistSelectManuscriptModule/apiError"];
    },

    // 複合的な状態
    hasAnyError(): boolean {
      return !!(this.error || this.apiError);
    },
    isAnyLoading(): boolean {
      return this.isApiLoading;
    },

    // パネル表示状態
    isVisible(): boolean {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },

    currentContentComponent(): string {
      const componentMap: Record<AssistContentType, string> = {
        request: "AssistRequestContent",
        "manuscript-select": "ManuscriptSelectContent",
        "theme-review-confirm": "ThemeReviewConfirmContent",
        "theme-matching-confirm": "ThemeMatchingConfirmContent",
        result: "ResultViewContent",
        history: "HistoryListContent",
      };
      return componentMap[this.currentContent] || "AssistRequestContent";
    },

    // 🆕 Phase 5: パフォーマンス最適化 - 計算キャッシュ対応
    currentContentProps(): Record<string, any> {
      const baseProps: Record<string, any> = {};
      const contentType = this.currentContent;

      // 🔧 条件分岐を最適化し、不要な計算を削減
      switch (contentType) {
        case "manuscript-select": {
          const novelId = this.$store.getters["assistSelectManuscriptModule/selectedNovelId"];
          return {
            ...baseProps,
            novelId, // Vuexから取得、デフォルトはnull
            useApi: true, // API機能を有効化
            requestType: this.currentRequestType, // リクエスト種別を渡す
          };
        }

        case "theme-review-confirm":
        case "theme-matching-confirm": {
          const novelId =
            this.$store.getters["assistSelectManuscriptModule/selectedNovelId"] ||
            this.$route?.params?.novelId ||
            null;
          return {
            ...baseProps,
            novelId,
          };
        }

        case "result": {
          // 結果表示画面用のprops
          return {
            ...baseProps,
            feedbackText: this.getResultFeedbackText(),
          };
        }

        case "history": {
          return {
            ...baseProps,
            // theme: 企画生成 + 企画レビュー + 企画マッチング。原稿: 企画系を除外（API は全件取得）
            functionType: null,
            includeFunctionTypes:
              this.context === "theme"
                ? [
                    AssistFunctionType.GENERATE_THEME,
                    AssistFunctionType.FEEDBACK_THEME,
                    AssistFunctionType.MATCHING_THEME,
                  ]
                : [],
            excludeFunctionTypes:
              this.context === "theme"
                ? []
                : [
                    AssistFunctionType.GENERATE_THEME,
                    AssistFunctionType.FEEDBACK_THEME,
                    AssistFunctionType.MATCHING_THEME,
                  ],
          };
        }

        case "request":
        default: {
          return {
            ...baseProps,
            context: this.context,
          };
        }
      }
    },

    minPanelWidth(): number {
      return ASSIST_PANEL_MIN_WIDTH;
    },
    maxPanelWidth(): number {
      return clampAssistPanelWidth(ASSIST_PANEL_MAX_WIDTH);
    },

    hasThemeGenerateRedirectRequest(): boolean {
      return this.context === "theme" && isThemeGenerateRedirectRequested(this.$route?.query);
    },
  },
  watch: {
    // ページ遷移で AssistPanel の context が変わっても Vuex の currentContent は残るため、
    // 当該 context で出せない画面なら依頼画面へ戻す
    context: {
      immediate: true,
      handler(ctx: AssistPanelContext) {
        if (!isContentAllowedForContext(this.currentContent, ctx)) {
          this.$store.dispatch("assistNavigationModule/showRequestContent");
        }
      },
    },

    activeTab: {
      immediate: true,
      handler(newTab: string) {
        // result / 企画項目生成の原稿選択中は、タブ再選択で依頼画面へ戻さない
        if (this.currentContent === "result" && newTab === "assist") {
          return;
        }
        if (
          this.currentContent === "manuscript-select" &&
          this.currentRequestType === "theme-generate" &&
          newTab === "assist"
        ) {
          return;
        }

        if (newTab === "assist") {
          // アシストタブがアクティブになった時はコンテンツを依頼画面に戻すのみ
          // パネルが表示中は原稿選択状態をリセットしない
          this.$store.dispatch("assistNavigationModule/showRequestContent");
          // assistSelectManuscriptModule の状態は維持する（非表示時のみクリア）
        }
      },
    },

    // 🔧 URL監視機能（Phase 2設計）- Phase 5 で最適化
    $route: {
      immediate: true,
      handler(newRoute: any, oldRoute?: any) {
        // 🔧 修正: result表示中はURL変更による自動リセットを抑制
        if (this.currentContent === "result") {
          return;
        }

        // アシストパネルが表示されている時のみデータ取得を実行
        if (this.isVisible) {
          this.handleRouteChange(newRoute, oldRoute);
        }
      },
    },

    // 🆕 パネル表示状態の監視（Phase 5 追加）
    isVisible: {
      handler(newVisible: boolean, oldVisible?: boolean) {
        if (newVisible && !oldVisible) {
          // パネル表示時のアナリティクス
          if (this.analytics) {
            this.analytics.trackPanelOpen();
          }
          this.initializePanelState();
        } else if (!newVisible && oldVisible) {
          // パネル非表示時のアナリティクス
          if (this.analytics) {
            this.analytics.trackPanelClose();
          }
          this.cleanupPanelState();
        }
      },
    },
  },
  methods: {
    handleTabChange(tabId: string): void {
      if (tabId === "close") {
        this.$store.dispatch("assistNavigationModule/hidePanel");
        this.$store.dispatch("assistSelectManuscriptModule/resetState");
        this.$emit("close");
        return;
      }

      // 1. Vuexのタブ状態を更新
      this.$store.dispatch("assistNavigationModule/setCurrentTab", tabId);

      // 2. タブ別のコンテンツ処理
      if (tabId === "assist") {
        this.$store.dispatch("assistNavigationModule/showRequestContent");
      } else if (tabId === "history") {
        this.$store.dispatch("assistNavigationModule/showHistory");
      }

      // 3. 親コンポーネントに通知
      this.$emit("tab-change", tabId);
    },

    handleSuggestionClick(suggestion: any): void {
      // 企画レビュー / 企画マッチングは AssistRequestContent 内で完結し suggestion-click を emit しない
      if (
        suggestion.id === "theme-feedback" ||
        suggestion.action === "request-theme-feedback" ||
        suggestion.id === "theme-matching" ||
        suggestion.action === "request-theme-matching"
      ) {
        return;
      }

      // リクエスト種別を保存
      if (suggestion.id === "feedback" || suggestion.action === "request-feedback") {
        this.currentRequestType = "feedback";
      } else if (suggestion.id === "matching" || suggestion.action === "request-matching") {
        this.currentRequestType = "matching";
      } else if (suggestion.id === "catchcopy" || suggestion.action === "request-catchcopy") {
        this.currentRequestType = "catchcopy";
      } else if (suggestion.id === "summary" || suggestion.action === "request-summary") {
        this.currentRequestType = "summary";
      } else if (suggestion.id === "theme-generate" || suggestion.action === "request-theme-generate") {
        this.currentRequestType = "theme-generate";
      }

      // 原稿選択画面に遷移
      this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
    },

    handleConfirmSelection(manuscript: any): void {
      // 🆕 Phase 5: ナビゲーションフローの改善
      this.executeNavigationFlow(manuscript);
    },

    // 🆕 統合ナビゲーションフローの実行（Phase 5 追加）
    async executeNavigationFlow(manuscript: any): Promise<void> {
      try {
        // 1. エラー状態をクリア
        this.clearAllErrors();

        // 2. 原稿情報を保存（必要に応じて）
        if (manuscript?.id) {
          await this.$store.dispatch("assistNavigationModule/showResultWithId", { id: manuscript.id });
        } else {
          await this.$store.dispatch("assistNavigationModule/showResult");
        }
      } catch (error) {
        logError({ module: "AssistPanel", action: "executeNavigationFlow" }, "フロー実行エラー", error);
        await this.$store.dispatch("assistNavigationModule/setError", "結果表示に失敗しました");
      }
    },

    handleManuscriptSelect(manuscript: any): void {
      this.$store.dispatch("assistSelectManuscriptModule/selectManuscript", manuscript);
    },

    handleManuscriptsLoaded(manuscripts: any[]): void {
      // Phase 3で削除されたアクションの代わりに、適切な処理を実装
    },

    handleManuscriptsError(error: any): void {
      logError({ module: "AssistPanel", action: "handleManuscriptsError" }, "原稿一覧の読み込みでエラーが発生", error);
      // Phase 3で削除されたアクションの代わりに、エラー表示処理を実装
      logError({ module: "AssistPanel", action: "handleManuscriptsError" }, "原稿一覧取得エラー", error);
    },

    handleBackToRequest(): void {
      this.$store.dispatch("assistNavigationModule/showRequestContent");
    },

    dismissErrors(): void {
      // 🆕 Phase 5: 統合エラーハンドリング
      this.clearAllErrors();
    },

    // 🆕 全てのエラー状態をクリアする統合メソッド（Phase 5 追加）
    clearAllErrors(): void {
      // 画面遷移関連のエラーをクリア（新しいアクションを追加）
      this.$store.dispatch("assistNavigationModule/clearError");
      // 原稿選択関連のエラーをクリア（新しいアクションを追加）
      this.$store.dispatch("assistSelectManuscriptModule/clearApiError");
    },

    getResultFeedbackText(): string {
      // selectedItemIdに基づいて適切な感想文を返す
      if (this.selectedItemId) {
        // assistHistoryから履歴データを取得
        const historyList = this.$store.getters["assistHistory/filteredHistoryList"];
        const selectedItem = historyList.find((item: any) => item.id === this.selectedItemId);

        if (selectedItem) {
          return (
            selectedItem.fullFeedback || selectedItem.feedbackPreview || "フィードバック内容が見つかりませんでした。"
          );
        }
        logWarn(
          { module: "AssistPanel", action: "getResultFeedbackText" },
          "指定されたIDの履歴アイテムが見つかりません",
          { selectedItemId: this.selectedItemId }
        );
        return `選択されたアイテム（AssistID: ${this.selectedItemId}）のデータが見つかりませんでした。\n\n履歴データが読み込まれていない可能性があります。過去ログタブから再度選択してください。`;
      }

      logWarn(
        { module: "AssistPanel", action: "getResultFeedbackText" },
        "指定されたIDの履歴アイテムが見つかりません",
        { selectedItemId: this.selectedItemId }
      );
      return `選択されたアイテム（AssistID: ${this.selectedItemId}）のデータが見つかりませんでした。\n\n履歴データが読み込まれていない可能性があります。過去ログタブから再度選択してください。`;
    },

    // 🆕 URL変化対応メソッド（Phase 2設計）
    async handleRouteChange(newRoute: any, oldRoute?: any) {
      try {
        // URLからnovelIdを抽出
        const novelId = this.extractNovelIdFromRoute(newRoute);

        // 企画項目生成でも原稿一覧が必要なため、theme でも initializeWithNovelId する
        if (novelId) {
          await this.$store.dispatch("assistSelectManuscriptModule/initializeWithNovelId", novelId);
        } else if (this.context === "theme") {
          await this.$store.dispatch("assistSelectManuscriptModule/resetState");
        } else {
          await this.$store.dispatch("assistSelectManuscriptModule/showNoManuscriptError");
        }
      } catch (error) {
        logError({ module: "AssistPanel", action: "handleRouteChange" }, "Route変化処理でエラー", error);
        if (this.context === "theme") {
          await this.$store.dispatch("assistSelectManuscriptModule/resetState");
        } else {
          await this.$store.dispatch("assistSelectManuscriptModule/showNoManuscriptError");
        }
      }
    },

    // 🆕 RouteからNovel IDを抽出するヘルパーメソッド
    extractNovelIdFromRoute(route: any): string | null {
      // 1. パラメータから抽出（例: /novels/:novelId/manuscripts）
      if (route.params?.novelId) {
        return route.params.novelId;
      }

      // 2. クエリパラメータから抽出（例: /manuscripts?novelId=123）
      if (route.query?.novelId) {
        return route.query.novelId;
      }

      // 3. パスから正規表現で抽出（実際のアプリ構造に対応）
      // Nolaアプリの実際の構造: /:novelId/editor, /:novelId/theme, /:novelId/detail など
      const pathMatch = route.path.match(
        /^\/([^/]+)\/(editor|theme|detail|characters|worldviews|materials|plot|timeline)/
      );
      if (pathMatch && pathMatch[1]) {
        return pathMatch[1];
      }

      // 4. Novel IDが見つからない場合
      return null;
    },

    // 🆕 パネル表示状態の初期化メソッド（Phase 5 追加） - 緊急修正版
    initializePanelState() {
      // 🔧 緊急修正: 重複処理を避ける（created()で既に実行済み）

      // URL変更検知を手動トリガー（必要に応じて）
      if (this.$route) {
        this.handleRouteChange(this.$route);
      }
    },

    // 🆕 パネル表示状態のクリーンアップメソッド（Phase 5 追加）
    cleanupPanelState() {
      // パネル非表示時の状態クリーンアップ処理を実装

      // 原稿選択状態をクリア（パネル非表示時のみ）
      this.$store.dispatch("assistSelectManuscriptModule/resetState");

      // 作家診断結果をクリア
      this.$store.dispatch("writerTypeResultModule/clearResult");

      // 必要に応じて他のクリーンアップ処理を追加
    },

    clampPanelWidth(width: number): number {
      return clampAssistPanelWidth(width);
    },

    onResizeStart(event: PointerEvent): void {
      if (!event.isPrimary || event.button !== 0) return;
      event.preventDefault();
      const handle = event.currentTarget as HTMLElement;
      handle.setPointerCapture(event.pointerId);
      this.isResizing = true;
      this.resizePointerId = event.pointerId;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = this.panelWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },

    onResizeMove(event: PointerEvent): void {
      if (!this.isResizing || event.pointerId !== this.resizePointerId) return;
      // 右サイドパネルなので、左へドラッグすると幅が増える
      const nextWidth = this.resizeStartWidth + (this.resizeStartX - event.clientX);
      this.panelWidth = this.clampPanelWidth(nextWidth);
    },

    onResizeEnd(event?: PointerEvent): void {
      if (!this.isResizing) return;
      if (event && this.resizePointerId != null && event.pointerId !== this.resizePointerId) return;

      const pointerId = event?.pointerId ?? this.resizePointerId;
      const target = event?.currentTarget;
      if (target instanceof HTMLElement && pointerId != null && target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }

      this.isResizing = false;
      this.resizePointerId = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      this.persistPanelWidth();
    },

    onResizeKeydown(event: KeyboardEvent): void {
      const step = event.shiftKey ? 40 : 16;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.panelWidth = this.clampPanelWidth(this.panelWidth + step);
        this.persistPanelWidthDebounced?.();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.panelWidth = this.clampPanelWidth(this.panelWidth - step);
        this.persistPanelWidthDebounced?.();
      }
    },

    onWindowResize(): void {
      this.panelWidth = this.clampPanelWidth(this.panelWidth);
    },

    persistPanelWidth(): void {
      saveAssistPanelWidth(this.panelWidth);
    },

    async consumeThemeGenerateRedirect(): Promise<void> {
      if (!this.hasThemeGenerateRedirectRequest) return;

      this.currentRequestType = "theme-generate";
      await this.$store.dispatch("assistNavigationModule/showPanel");
      await this.$store.dispatch("assistNavigationModule/showManuscriptSelect");

      try {
        await this.$router.replace({
          path: this.$route.path,
          query: removeThemeGenerateRedirectQuery(this.$route.query),
          hash: this.$route.hash,
        });
      } catch (error) {
        logWarn(
          { module: "AssistPanel", action: "consumeThemeGenerateRedirect" },
          "企画項目生成の遷移パラメータ削除に失敗しました",
          error
        );
      }
    },
  },

  // 🆕 Phase 5: パネル表示制御の最適化 - 緊急修正版
  // コンポーネント初期化時は基本的な状態管理を実行
  created() {
    // Analytics初期化
    this.analytics = new AssistAnalytics(this.$gtm);

    // 🔧 修正: 結果表示中でない場合のみ初期化を実行
    if (this.currentContent !== "result") {
      // Vuex状態の初期化（ナビゲーション状態のみ、原稿選択状態は維持）
      this.$store.dispatch("assistNavigationModule/resetState");
      // assistSelectManuscriptModule の状態は維持する（パネル表示中は保持）
    }

    // パネルが既に表示状態の場合は追加の初期化を実行
    if (this.isVisible) {
      this.initializePanelState();
    }

    this.panelWidth = this.clampPanelWidth(this.panelWidth);
    this.persistPanelWidthDebounced = debounce(() => {
      saveAssistPanelWidth(this.panelWidth);
    }, 200);
    window.addEventListener("resize", this.onWindowResize);
  },

  mounted() {
    this.$nextTick(() => {
      this.consumeThemeGenerateRedirect();
    });
  },

  beforeDestroy() {
    this.onResizeEnd();
    this.persistPanelWidthDebounced?.flush();
    this.persistPanelWidthDebounced?.cancel();
    window.removeEventListener("resize", this.onWindowResize);

    // アシスト履歴のポーリング停止
    this.$store.dispatch("assistHistory/stopPolling");

    // アシスト関連モジュールの状態リセット
    this.$store.dispatch("assistNavigationModule/resetState");
    this.$store.dispatch("assistSelectManuscriptModule/resetState");
    this.$store.dispatch("assistHistory/resetState");
    this.$store.dispatch("writerTypeResultModule/clearResult");
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.assist-panel {
  /* 幅は inline style（panelWidth）で指定。親グリッドは auto 列で追従する */
  box-sizing: border-box;
  flex-shrink: 0;
  /* 親グリッド行（minmax(0,1fr)）に合わせて伸ばす。内容高さに伸ばさない */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: var(--color-surface-subtle);
  border-left: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  position: relative; /* オーバーレイ・リサイズハンドルの位置基準 */
}

.assist-panel__resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  z-index: 8;
  cursor: col-resize;
  touch-action: none;
  background: transparent;

  &:hover,
  &:focus-visible,
  .assist-panel--resizing & {
    background-color: var(--color-primary);
    opacity: 0.35;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px var(--color-focus-ring);
    opacity: 1;
  }
}

.assist-panel__header {
  flex-shrink: 0;
}

.assist-panel__content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  background-color: var(--color-surface-subtle);
  min-height: 0; /* flexbox の overflow を適切に機能させる */

  /* ManuscriptSelectContentの依頼ボタンがはみ出さないように調整 */
  .manuscript-select-content {
    .manuscript-select-content__actions {
      padding: 0 8px; /* 左右に少し余白を追加 */
    }
  }
}

.assist-panel__footer {
  flex-shrink: 0;
  border-top: 1px solid var(--color-border);
  position: sticky; /* FishCounter を下部に固定 */
  bottom: 0;
  background-color: var(--color-surface-subtle); /* 背景色を明示 */
  z-index: 5; /* コンテンツより前面に表示 */
}

.assist-panel__error {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 10;
}

.error-content {
  background: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  [data-app-theme="dark"] & {
    background: #4a2020;
    border-color: #c53030;
  }
}

.error-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.error-messages {
  flex: 1;
}

.error-message,
.api-error-message {
  margin: 0;
  font-size: var(--font-size-sm);
  color: #c53030;
  line-height: 1.4;

  [data-app-theme="dark"] & {
    color: #fc8181;
  }
}

.api-error-message {
  margin-top: 4px;
  font-size: var(--font-size-xs);
  opacity: 0.8;
}

.error-dismiss {
  position: relative;
  background: none;
  border: none;
  font-size: var(--font-size-md);
  color: #c53030;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-xs);
  transition: background-color 0.2s;
  @include hover-overlay;

  [data-app-theme="dark"] & {
    color: #fc8181;
  }
}

.assist-panel__loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;

  [data-app-theme="dark"] & {
    background: rgba(36, 36, 36, 0.8);
  }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: var(--radius-rounded);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-content p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  text-align: center;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .assist-panel {
    width: 100% !important;
    height: 100%;
    min-height: 400px;
  }

  .assist-panel__resize-handle {
    display: none;
  }
}

@media (max-width: 576px) {
  .assist-panel__content {
    padding: 8px;
  }

  .assist-panel__footer {
    padding: 8px;
  }
}

/* パネル表示/非表示のトランジション効果 */
.assist-panel-fade-enter-active,
.assist-panel-fade-leave-active {
  transition: opacity 0.3s ease;
}

.assist-panel-fade-enter {
  opacity: 0;
}

.assist-panel-fade-leave-to {
  opacity: 0;
}
</style>
